import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { api } from '../api';
import { LightAppPageShell } from '../layout/LightAppPageShell';

interface AnalyticsSummary {
  totalEvents: number;
  totalSessions: number;
  totalPricingTables: number;
  eventTypes: Array<{ type: string; count: number }>;
  sessionStats: {
    averagePageDuration: number;
    averageTableVisibleDuration: number;
    totalSessions: number;
  };
  trackingStats: {
    ctaClicks: number;
    tableClicks: number;
    tableHovers: number;
  };
}

interface RealTimeAnalytics {
  activeSessions: number;
  recentEvents: number;
  todayStats: {
    totalSessions: number;
    averageDuration: number;
  };
}

interface EnhancedTrackingAnalytics {
  ctaClicks: number;
  tableClicks: number;
  tableHovers: number;
  billingPeriodViews: any[];
  deviceStats: Array<{ deviceType: string; count: number }>;
  browserStats: Array<{ browserType: string; count: number }>;
}

interface Session {
  id: string;
  session_id: string;
  pricing_table_id: string;
  template_filled_id: string;
  visitor_ip: string;
  user_agent: string;
  page_load_time: string;
  total_page_duration_seconds: number;
  table_visible_duration_seconds: number;
  is_active: boolean;
  clicked_cta: boolean;
  clicked_table: boolean;
  hover_table: boolean;
  clicked_link_1: string | null;
  clicked_link_2: string | null;
  clicked_link_3: string | null;
  monthly_billing_period_viewed: string;
  annually_billing_period_viewed: string;
  quarterly_billing_period_viewed: string;
  lifetime_billing_period_viewed: string;
  browser_type: string | null;
  browser_version: string | null;
  device_type: string | null;
  operating_system: string | null;
  user: {
    email: string;
  };
}

const AnalyticsDashboardPage: React.FC = () => {
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [realTime, setRealTime] = useState<RealTimeAnalytics | null>(null);
  const [enhancedTracking, setEnhancedTracking] = useState<EnhancedTrackingAnalytics | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadAnalytics();
    const interval = setInterval(loadRealTimeAnalytics, 30000); // Update every 30 seconds
    return () => clearInterval(interval);
  }, [timeRange]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const [summaryRes, realTimeRes, enhancedRes, sessionsRes] = await Promise.all([
        api.get(`/analytics/summary?days=${timeRange}`),
        api.get('/analytics/realtime'),
        api.get(`/analytics/enhanced-tracking?days=${timeRange}`),
        api.get('/analytics/sessions?limit=20')
      ]);

      setSummary(summaryRes.data.data);
      setRealTime(realTimeRes.data.data);
      setEnhancedTracking(enhancedRes.data.data);
      setSessions(sessionsRes.data.data.sessions);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRealTimeAnalytics = async () => {
    try {
      const response = await api.get('/analytics/realtime');
      setRealTime(response.data.data);
    } catch (error) {
      console.error('Error loading real-time analytics:', error);
    }
  };

  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  const getBillingPeriodStatus = (status: string): { label: string; color: string } => {
    switch (status) {
      case 'V': return { label: 'Viewed', color: 'bg-blue-100 text-blue-800' };
      case 'C': return { label: 'Clicked', color: 'bg-green-100 text-green-800' };
      default: return { label: 'Not Viewed', color: 'bg-gray-100 text-gray-800' };
    }
  };

  if (loading) {
    return (
      <LightAppPageShell>
        <div className="flex min-h-[16rem] items-center justify-center">
          <div className="text-lg text-gray-700">Loading analytics...</div>
        </div>
      </LightAppPageShell>
    );
  }

  return (
    <LightAppPageShell>
    <div className="space-y-6 py-2 [&_.rounded-lg.border]:border-gray-200 [&_.rounded-lg.border]:bg-white [&_.rounded-lg.border]:text-gray-900 [&_.rounded-lg.border]:shadow-sm">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
        <div className="flex items-center space-x-4">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={loadAnalytics} variant="outline">
            Refresh
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid h-auto w-full grid-cols-2 gap-1 rounded-lg border border-gray-200 bg-gray-100 p-1 text-gray-700 shadow-inner sm:grid-cols-4 [&_[data-state=active]]:bg-white [&_[data-state=active]]:text-gray-900 [&_[data-state=active]]:shadow-sm [&_[data-state=inactive]]:text-gray-600">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="realtime">Real-time</TabsTrigger>
          <TabsTrigger value="tracking">Enhanced Tracking</TabsTrigger>
          <TabsTrigger value="sessions">Sessions</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Events</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{summary?.totalEvents || 0}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Sessions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{summary?.totalSessions || 0}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Pricing Tables</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{summary?.totalPricingTables || 0}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">CTA Clicks</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{summary?.trackingStats.ctaClicks || 0}</div>
              </CardContent>
            </Card>
          </div>

          {/* Session Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Session Statistics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between">
                  <span>Average Page Duration:</span>
                  <span className="font-medium">
                    {formatDuration(summary?.sessionStats.averagePageDuration || 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Average Table Visible Duration:</span>
                  <span className="font-medium">
                    {formatDuration(summary?.sessionStats.averageTableVisibleDuration || 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Table Clicks:</span>
                  <span className="font-medium">{summary?.trackingStats.tableClicks || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>Table Hovers:</span>
                  <span className="font-medium">{summary?.trackingStats.tableHovers || 0}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Event Types</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {summary?.eventTypes.map((eventType) => (
                    <div key={eventType.type} className="flex justify-between">
                      <span className="capitalize">{eventType.type.replace('_', ' ')}:</span>
                      <span className="font-medium">{eventType.count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="realtime" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Active Sessions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600">
                  {realTime?.activeSessions || 0}
                </div>
                <p className="text-sm text-gray-600">Last 5 minutes</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Recent Events</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-600">
                  {realTime?.recentEvents || 0}
                </div>
                <p className="text-sm text-gray-600">Last hour</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Today's Sessions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-purple-600">
                  {realTime?.todayStats.totalSessions || 0}
                </div>
                <p className="text-sm text-gray-600">
                  Avg: {formatDuration(realTime?.todayStats.averageDuration || 0)}
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="tracking" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Interaction Tracking</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between">
                  <span>CTA Clicks:</span>
                  <Badge variant="default">{enhancedTracking?.ctaClicks || 0}</Badge>
                </div>
                <div className="flex justify-between">
                  <span>Table Clicks:</span>
                  <Badge variant="secondary">{enhancedTracking?.tableClicks || 0}</Badge>
                </div>
                <div className="flex justify-between">
                  <span>Table Hovers:</span>
                  <Badge variant="outline">{enhancedTracking?.tableHovers || 0}</Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Device Statistics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {enhancedTracking?.deviceStats.map((device) => (
                    <div key={device.deviceType} className="flex justify-between">
                      <span className="capitalize">{device.deviceType}:</span>
                      <span className="font-medium">{device.count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Browser Statistics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {enhancedTracking?.browserStats.map((browser) => (
                  <div key={browser.browserType} className="text-center">
                    <div className="text-lg font-semibold">{browser.browserType}</div>
                    <div className="text-sm text-gray-600">{browser.count} sessions</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sessions" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Recent Sessions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {sessions.map((session) => (
                  <div key={session.id} className="border rounded-lg p-4 space-y-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium">Session {session.session_id.slice(0, 8)}...</div>
                        <div className="text-sm text-gray-600">
                          {formatDate(session.page_load_time)}
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        {session.clicked_cta && <Badge variant="default">CTA Clicked</Badge>}
                        {session.clicked_table && <Badge variant="secondary">Table Clicked</Badge>}
                        {session.hover_table && <Badge variant="outline">Table Hovered</Badge>}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Duration:</span>
                        <div className="font-medium">{formatDuration(session.total_page_duration_seconds)}</div>
                      </div>
                      <div>
                        <span className="text-gray-600">Table Visible:</span>
                        <div className="font-medium">{formatDuration(session.table_visible_duration_seconds)}</div>
                      </div>
                      <div>
                        <span className="text-gray-600">Device:</span>
                        <div className="font-medium capitalize">{session.device_type || 'Unknown'}</div>
                      </div>
                      <div>
                        <span className="text-gray-600">Browser:</span>
                        <div className="font-medium">{session.browser_type || 'Unknown'}</div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <div className="text-xs">
                        <span className="text-gray-600">Monthly:</span>
                        <Badge className={`ml-1 ${getBillingPeriodStatus(session.monthly_billing_period_viewed).color}`}>
                          {getBillingPeriodStatus(session.monthly_billing_period_viewed).label}
                        </Badge>
                      </div>
                      <div className="text-xs">
                        <span className="text-gray-600">Annually:</span>
                        <Badge className={`ml-1 ${getBillingPeriodStatus(session.annually_billing_period_viewed).color}`}>
                          {getBillingPeriodStatus(session.annually_billing_period_viewed).label}
                        </Badge>
                      </div>
                      <div className="text-xs">
                        <span className="text-gray-600">Quarterly:</span>
                        <Badge className={`ml-1 ${getBillingPeriodStatus(session.quarterly_billing_period_viewed).color}`}>
                          {getBillingPeriodStatus(session.quarterly_billing_period_viewed).label}
                        </Badge>
                      </div>
                      <div className="text-xs">
                        <span className="text-gray-600">Lifetime:</span>
                        <Badge className={`ml-1 ${getBillingPeriodStatus(session.lifetime_billing_period_viewed).color}`}>
                          {getBillingPeriodStatus(session.lifetime_billing_period_viewed).label}
                        </Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
    </LightAppPageShell>
  );
};

export default AnalyticsDashboardPage;
